import React, { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";

export default function ObdMasterTable() {
    const [pids, setPids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const q = query(collection(db, "OBD_PID_MASTER"), orderBy("pidHex"));
        const unsubscribe = onSnapshot(q, (snap) => {
            setPids(snap.docs.map(d => d.data()));
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredPids = pids.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.pidHex.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

            {/* Header */}
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-white">
                <div>
                    <h2 className="text-xl font-black text-slate-800">OBD Master Chart</h2>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wide">Standard PID Definitions</p>
                </div>

                {/* Search */}
                <div className="w-1/3">
                    <input
                        type="text"
                        placeholder="Search by PID (e.g. 0x0C) or Name..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all placeholder:font-medium placeholder:text-slate-400"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/30">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-3">
                        <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs font-bold text-slate-400">Loading Master Data...</span>
                    </div>
                ) : (
                    <table className="w-full text-left text-xs bg-white">
                        <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 sticky top-0 z-10">
                            <tr>
                                <th className="p-3 w-20">Hex</th>
                                <th className="p-3 w-16">Dec</th>
                                <th className="p-3">Parameter Name</th>
                                <th className="p-3 w-24">Bytes</th>
                                <th className="p-3 w-24">Unit</th>
                                <th className="p-3 w-24">Type</th>
                                <th className="p-3">Expression</th>
                                <th className="p-3">Arduino</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredPids.map((pid) => {
                                const formula = pid.formula || {};
                                const type = typeof formula === 'object' ? formula.type : 'legacy';
                                const expr = typeof formula === 'object' ? formula.expr : formula;
                                const arduino = typeof formula === 'object' ? formula.arduino : '-';

                                return (
                                    <tr key={pid.pidHex} className="hover:bg-indigo-50/50 transition-colors group">
                                        <td className="p-3 font-mono font-bold text-indigo-600">{pid.pidHex}</td>
                                        <td className="p-3 font-mono text-slate-400">{pid.pidDec}</td>
                                        <td className="p-3 font-medium text-slate-700 group-hover:text-indigo-900">{pid.name}</td>
                                        <td className="p-3 text-slate-500">{pid.bytes}</td>
                                        <td className="p-3 text-slate-500">{pid.unit || "-"}</td>

                                        {/* Formula Columns */}
                                        <td className="p-3">
                                            <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold">
                                                {type || "-"}
                                            </span>
                                        </td>
                                        <td className="p-3 font-mono text-slate-500 text-[10px]">{expr || "-"}</td>
                                        <td className="p-3 font-mono text-slate-500 text-[10px]">{arduino || "-"}</td>
                                    </tr>
                                );
                            })}
                            {filteredPids.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="p-8 text-center text-slate-400 font-medium italic">
                                        No parameters found matching "{searchTerm}"
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-100 bg-white text-[10px] text-slate-400 text-center font-medium">
                Note: This list is maintained globally for all models.
            </div>
        </div>
    );
}
